import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';
import Layout from './components/layout/Layout';
import AuthTabs from './pages/auth/AuthTabs';
import BusinessLandingPage from './pages/auth/BusinessLandingPage';
import BusinessSignupPage from './pages/auth/BusinessSignupPage';
import ForgotPassword from './pages/ForgotPassword';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsPage from './pages/legal/TermsPage';
import Dashboard from './pages/DashboardPage';
import CalendarPage from './pages/CalendarPage';
import Profile from './pages/Profile';
import Classes from './pages/Classes';
import InstructorsShowcasePage from './pages/InstructorsShowcasePage';
import UserCodePage from './pages/UserCodePage';
import UserMembershipsPage from './pages/UserMembershipsPage';
import HablanosPage from './pages/HablanosPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import AdminInstructors from './pages/admin/Instructors';
import AdminClassTypes from './pages/admin/ClassTypes';
import UserManagementPage from './pages/admin/UserManagementPage';
import ClassPassManagementPage from './pages/admin/ClassPassManagementPage';
import AnnouncementManagementPage from './pages/admin/AnnouncementManagementPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import StatisticsPage from './pages/admin/StatisticsPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import CalendarManagementPage from './pages/admin/CalendarManagementPage';
import QRReaderPage from './pages/admin/QRReaderPage';
import BusinessConfigPage from './pages/admin/BusinessConfigPage';
import WODManagementPage from './pages/admin/WODManagementPage';
import WODPage from './pages/WODPage';
import SuperAdminDashboardPage from './pages/super/SuperAdminDashboardPage';
import SuperAdminBusinessesPage from './pages/super/SuperAdminBusinessesPage';
import SuperAdminMaintenancePage from './pages/super/SuperAdminMaintenancePage';
import SuperAdminUsersPage from './pages/super/SuperAdminUsersPage';
import SuperAdminCommunicationsPage from './pages/super/SuperAdminCommunicationsPage';
import SuperAdminConfigPage from './pages/super/SuperAdminConfigPage';
import SuperAdminLogsPage from './pages/super/SuperAdminLogsPage';
import {
  InstructorDashboard,
  InstructorClasses,
  InstructorClassDetail,
  InstructorHistory,
  InstructorProfile,
  InstructorAttendance
} from './pages/instructor';
import './index.css';

// Componente para rutas protegidas de usuario normal
const UserRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Si es admin, super admin o instructor, redirigir a su panel correspondiente
  if (currentUser.role === 'super_admin') {
    return <Navigate to="/super/overview" replace />;
  }
  if (currentUser.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (currentUser.role === 'instructor') {
    return <Navigate to="/instructor" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas de admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Verificar rol de admin
  if (currentUser.role !== 'admin' && currentUser.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas de super admin
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.role !== 'super_admin') {
    return <Navigate to="/admin/analytics" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas de instructor
const InstructorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Solo permitir acceso a instructores
  if (currentUser.role !== 'instructor') {
    // Redirigir según el rol
    if (currentUser.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (currentUser.role === 'super_admin') {
      return <Navigate to="/super/overview" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Componente para redirigir /register/:slug a /b/:slug (compatibilidad)
const RegisterRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/b/${slug}`} replace />;
};

const AppRoutes: React.FC = () => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Función para determinar la ruta de destino inicial
  const getInitialRoute = () => {
    if (currentUser?.role === 'super_admin') {
      return '/super/overview';
    }
    if (currentUser?.role === 'admin') {
      return '/admin/dashboard';
    }
    if (currentUser?.role === 'instructor') {
      return '/instructor';
    }
    return '/dashboard';
  };

  // Manejar botón de retroceso en PWA
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (!isStandalone) return;

    const rootPages = ['/', '/dashboard', '/admin/dashboard', '/super/overview', '/instructor'];
    const isRootPage = rootPages.includes(location.pathname);

    const handlePopState = () => {
      if (currentUser && isRootPage) {
        // Evitar salir de la app - volver a agregar al historial
        history.pushState(null, '', window.location.href);
      }
    };

    // Agregar estado al historial en páginas raíz
    if (currentUser && isRootPage) {
      history.pushState(null, '', window.location.href);
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname, currentUser]);

  return (
    <Routes>
      {/* Ruta raíz - redirigir según autenticación y rol */}
      <Route path="/" element={
        loading ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : currentUser ? (
          <Navigate to={getInitialRoute()} replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />

      {/* Rutas públicas - redirigir si ya está autenticado */}
      <Route path="/login" element={
        loading ? (
          <div className="min-h-screen flex items-center justify-center bg-gray-950">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : currentUser ? (
          <Navigate to={getInitialRoute()} replace />
        ) : (
          <AuthTabs />
        )
      } />
      <Route path="/register" element={
        loading ? (
          <div className="min-h-screen flex items-center justify-center bg-gray-950">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : currentUser ? (
          <Navigate to={getInitialRoute()} replace />
        ) : (
          <AuthTabs />
        )
      } />
      {/* Nueva ruta: Landing de negocio /b/:slug */}
      <Route path="/b/:slug" element={<BusinessLandingPage />} />

      {/* Ruta para registro de negocios */}
      <Route path="/business-signup" element={
        loading ? (
          <div className="min-h-screen flex items-center justify-center bg-gray-950">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : currentUser ? (
          <Navigate to={getInitialRoute()} replace />
        ) : (
          <BusinessSignupPage />
        )
      } />

      {/* Compatibilidad: /register/:slug redirige a /b/:slug */}
      <Route path="/register/:slug" element={<RegisterRedirect />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />

      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Rutas legales - públicas */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Rutas de usuario normal */}
      <Route path="/dashboard" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<Dashboard />} />
      </Route>

      <Route path="/calendar" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<CalendarPage />} />
      </Route>

      <Route path="/classes" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<Classes />} />
      </Route>

      <Route path="/instructors" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<InstructorsShowcasePage />} />
      </Route>

      <Route path="/my-code" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<UserCodePage />} />
      </Route>

      <Route path="/profile" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<Profile />} />
      </Route>

      <Route path="/memberships" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<UserMembershipsPage />} />
      </Route>

      <Route path="/chat" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<HablanosPage />} />
      </Route>

      <Route path="/announcements" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<AnnouncementsPage />} />
      </Route>

      <Route path="/wod" element={
        <UserRoute>
          <Layout />
        </UserRoute>
      }>
        <Route index element={<WODPage />} />
      </Route>

      {/* Rutas de administración */}
      <Route path="/admin" element={
        <AdminRoute>
          <Layout />
        </AdminRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboardPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="statistics" element={<StatisticsPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="instructors" element={<AdminInstructors />} />
        <Route path="class-types" element={<AdminClassTypes />} />
        <Route path="class-passes" element={<ClassPassManagementPage />} />
        <Route path="announcements" element={<AnnouncementManagementPage />} />
        <Route path="calendar-management" element={<CalendarManagementPage />} />
        <Route path="qr-reader" element={<QRReaderPage />} />
        <Route path="config" element={<BusinessConfigPage />} />
        <Route path="wods" element={<WODManagementPage />} />
      </Route>

      {/* Rutas de instructor */}
      <Route path="/instructor" element={
        <InstructorRoute>
          <Layout />
        </InstructorRoute>
      }>
        <Route index element={<InstructorDashboard />} />
        <Route path="classes" element={<InstructorClasses />} />
        <Route path="class/:classId" element={<InstructorClassDetail />} />
        <Route path="attendance" element={<InstructorAttendance />} />
        <Route path="history" element={<InstructorHistory />} />
        <Route path="profile" element={<InstructorProfile />} />
      </Route>

      {/* Rutas de super administración */}
      <Route path="/super" element={
        <SuperAdminRoute>
          <Layout />
        </SuperAdminRoute>
      }>
        <Route index element={<Navigate to="/super/overview" replace />} />
        <Route path="overview" element={<SuperAdminDashboardPage />} />
        <Route path="businesses" element={<SuperAdminBusinessesPage />} />
        <Route path="users" element={<SuperAdminUsersPage />} />
        <Route path="communications" element={<SuperAdminCommunicationsPage />} />
        <Route path="config" element={<SuperAdminConfigPage />} />
        <Route path="logs" element={<SuperAdminLogsPage />} />
        <Route path="maintenance" element={<SuperAdminMaintenancePage />} />
      </Route>

      {/* Ruta por defecto */}
      <Route path="*" element={
        loading ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : currentUser ? (
          <Navigate to={getInitialRoute()} replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />
    </Routes>
  );
};

const App: React.FC = () => {
  // Redirección de dominio
  useEffect(() => {
    const hostname = window.location.hostname;
    const targetDomain = 'sport.reserberry.com';
    const oldDomains = [
      'gestion-gym-d9267.web.app',
      'gestion-gym-d9267.firebaseapp.com'
    ];

    if (oldDomains.includes(hostname)) {
      const newUrl = `https://${targetDomain}${window.location.pathname}${window.location.search}`;
      window.location.replace(newUrl);
    }
  }, []);

  return (
    <BusinessProvider>
      <AppRoutes />
    </BusinessProvider>
  );
};

export default App; 